import type { ReactNode } from "react";

interface FilterBarProps {
  typeOptions: string[];
  regionOptions: string[];
  yearOptions: number[];
  selectedTypes: string[];
  selectedRegions: string[];
  selectedYears: number[];
  minRating: number;
  onToggleType: (value: string) => void;
  onToggleRegion: (value: string) => void;
  onToggleYear: (value: number) => void;
  onRatingChange: (value: number) => void;
  onReset: () => void;
}

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors md:text-sm ${
        active
          ? "border-primary-500 bg-primary-500 text-background-50"
          : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-foreground-900"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterBar({
  typeOptions,
  regionOptions,
  yearOptions,
  selectedTypes,
  selectedRegions,
  selectedYears,
  minRating,
  onToggleType,
  onToggleRegion,
  onToggleYear,
  onRatingChange,
  onReset,
}: FilterBarProps) {
  const hasActive =
    selectedTypes.length > 0 ||
    selectedRegions.length > 0 ||
    selectedYears.length > 0 ||
    minRating > 0;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-16 shrink-0 pt-1 text-xs font-semibold text-foreground-500 md:text-sm">
            类型
          </span>
          <div className="flex flex-wrap gap-2">
            {typeOptions.map((opt) => (
              <Chip
                key={opt}
                active={selectedTypes.includes(opt)}
                onClick={() => onToggleType(opt)}
              >
                {opt}
              </Chip>
            ))}
          </div>
        </div>

        <div className="h-px w-full bg-background-200"></div>

        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-16 shrink-0 pt-1 text-xs font-semibold text-foreground-500 md:text-sm">
            地区
          </span>
          <div className="flex flex-wrap gap-2">
            {regionOptions.map((opt) => (
              <Chip
                key={opt}
                active={selectedRegions.includes(opt)}
                onClick={() => onToggleRegion(opt)}
              >
                {opt}
              </Chip>
            ))}
          </div>
        </div>

        <div className="h-px w-full bg-background-200"></div>

        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-16 shrink-0 pt-1 text-xs font-semibold text-foreground-500 md:text-sm">
            年份
          </span>
          <div className="flex flex-wrap gap-2">
            {yearOptions.map((opt) => (
              <Chip
                key={opt}
                active={selectedYears.includes(opt)}
                onClick={() => onToggleYear(opt)}
              >
                {opt} 年
              </Chip>
            ))}
          </div>
        </div>

        <div className="h-px w-full bg-background-200"></div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-foreground-500 md:text-sm">
              最低评分
            </span>
            <div className="flex flex-wrap gap-2">
              {[0, 7, 8, 8.5].map((score) => (
                <Chip
                  key={score}
                  active={minRating === score}
                  onClick={() => onRatingChange(score)}
                >
                  {score === 0 ? "不限" : `${score} 分以上`}
                </Chip>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onReset}
            disabled={!hasActive}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-medium transition-colors md:text-sm ${
              hasActive
                ? "border-accent-300 bg-accent-50 text-accent-800 hover:bg-accent-100"
                : "cursor-not-allowed border-background-200 text-foreground-400"
            }`}
          >
            <i className="ri-refresh-line text-base"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}